import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#syncActiveSlideBackground(0);
    // suspend() inainte de play(): daca elementul e reconectat (morph la hidratarea
    // headerului), fara asta ar ramane doua intervale in paralel si bara ar sari
    // cate doua mesaje deodata.
    this.suspend();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Fara asta, intervalul si ascultatorul de pe document supravietuiau elementului
    // la fiecare re-randare de sectiune.
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Propagate the active slide's custom background color (--slide-bg)
   * to the surrounding announcement bar so it fills the entire bar.
   * @param {number} activeIndex
   */
  #syncActiveSlideBackground(activeIndex) {
    const bar = this.closest('.announcement-bar');
    if (!bar) return;
    const activeSlide = this.refs.slides?.[activeIndex];
    const bg = activeSlide?.style.getPropertyValue('--slide-bg')?.trim();
    if (bg) {
      bar.style.setProperty('--slide-bg-active', bg);
      bar.setAttribute('data-slide-bg', '');
    } else {
      bar.style.removeProperty('--slide-bg-active');
      bar.removeAttribute('data-slide-bg');
    }
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay) return;

    // 22.08.2026, reparat: aici era `if (!this.autoplay || this.paused) return;`.
    // `#handleVisibilityChange` cheama `pause()` cand tabul e ascuns, iar `pause()`
    // pune `paused = true`. La revenirea in tab, `resume()` vedea `paused` si iesea
    // imediat, deci bara ramanea inghetata pe primul mesaj pentru tot restul sesiunii.
    // Cu 3 mesaje in bara, doua nu mai apareau niciodata, inclusiv cel de Klarna.
    // `play()` pune la loc `paused = false`, deci nu mai e nevoie de `pause()` aici.
    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    this.#syncActiveSlideBackground(relativeIndex);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
